import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import RefreshIcon from "@material-ui/icons/Refresh";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import MarkunreadOutlinedIcon from "@material-ui/icons/MarkunreadOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import WarningIcon from "@material-ui/icons/Warning";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Recarga automatica do quadro. Painel gerencial nao precisa ser instantaneo, e
// intervalo fixo mantem a carga no banco previsivel - diferente de reagir a cada
// evento de socket numa operacao com centenas de mensagens por minuto.
const REFRESH_INTERVAL_MS = 30000;

const useStyles = makeStyles(theme => ({
  metricCard: {
    padding: theme.spacing(1.25),
    borderRadius: 14,
    minHeight: 68,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19, 31, 43, 0.98) 0%, rgba(13, 24, 34, 0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,250,249,0.98) 100%)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.16)"
        : "1px solid rgba(18, 140, 126, 0.10)"
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },
  metricValue: {
    fontSize: "1.5rem",
    fontWeight: 800,
    lineHeight: 1.1
  },
  iconMuted: {
    color: theme.palette.text.secondary,
    display: "flex"
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5)
  },
  updatedAt: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem"
  },
  truncatedWarning: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 12,
    marginBottom: theme.spacing(1.5),
    backgroundColor: "#FFF7ED",
    color: "#92400E",
    fontWeight: 700,
    fontSize: "0.82rem"
  },
  // Rolagem horizontal e o coracao do formato: as colunas continuam para o lado
  // em vez de espremer, entao uma operacao com 20 atendentes nao vira 20 tiras
  // ilegiveis.
  // flex:1 + minHeight:0 em vez de calc(100vh - N): o MainContainer ja e uma
  // coluna flex de altura limitada, entao o quadro ocupa exatamente o que
  // sobra. O calc anterior era um chute que estourava a viewport - a coluna
  // passava do fim da tela e as ultimas linhas ficavam inalcancaveis.
  boardScroller: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: theme.spacing(1),
    alignItems: "stretch",
    flex: 1,
    minHeight: 0
  },
  column: {
    flex: "0 0 272px",
    maxWidth: 272,
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    maxHeight: "100%",
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.55)"
        : "rgba(241, 245, 249, 0.75)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.12)"
        : "1px solid rgba(148, 163, 184, 0.22)"
  },
  columnPending: {
    background:
      theme.palette.type === "dark"
        ? "rgba(69, 26, 3, 0.35)"
        : "rgba(255, 247, 237, 0.95)",
    border: "1px solid rgba(234, 179, 8, 0.35)"
  },
  columnHeader: {
    padding: theme.spacing(1),
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.12)"
        : "1px solid rgba(148, 163, 184, 0.22)"
  },
  columnTitle: {
    fontWeight: 800,
    fontSize: "0.86rem",
    display: "flex",
    alignItems: "center",
    gap: 6
  },
  presenceDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flex: "0 0 auto"
  },
  columnCounters: {
    display: "flex",
    gap: 6,
    marginTop: 6,
    flexWrap: "wrap"
  },
  counterChip: {
    fontSize: "0.66rem",
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    background:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.18)"
        : "rgba(148, 163, 184, 0.20)"
  },
  counterChipUnread: {
    background: "rgba(239, 68, 68, 0.18)",
    color: "#B91C1C"
  },
  columnBody: {
    padding: theme.spacing(0.75),
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75)
  },
  card: {
    padding: theme.spacing(0.9),
    borderRadius: 10,
    cursor: "pointer",
    background: theme.palette.type === "dark" ? "#0f172a" : "#ffffff",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.28)",
    transition: "transform .12s ease, box-shadow .12s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)"
    }
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6
  },
  cardName: {
    fontWeight: 700,
    fontSize: "0.81rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  cardTicketId: {
    fontSize: "0.67rem",
    fontWeight: 700,
    color: theme.palette.text.secondary
  },
  cardPreview: {
    marginTop: 2,
    fontSize: "0.73rem",
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  cardMeta: {
    marginTop: 4,
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    alignItems: "center"
  },
  metaChip: {
    fontSize: "0.64rem",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 999,
    background:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.16)"
        : "rgba(148, 163, 184, 0.18)"
  },
  metaChipStale: {
    background: "rgba(239, 68, 68, 0.16)",
    color: "#B91C1C"
  },
  unreadBadge: {
    fontSize: "0.68rem",
    fontWeight: 800,
    minWidth: 20,
    textAlign: "center",
    padding: "1px 6px",
    borderRadius: 999,
    background: "#ef4444",
    color: "#fff"
  },
  emptyColumn: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.8rem"
  }
}));

const PRESENCE_COLORS = {
  online: "#22c55e",
  away: "#f59e0b",
  offline: "#94a3b8"
};

// "há 2 minutos" e o dado que o gerente usa para decidir onde olhar, entao vale
// mais que o horario exato. Sem dependencia nova: date-fns ja esta no projeto,
// mas para um rotulo tao curto a conta direta e mais previsivel que locale.
const formatAge = value => {
  if (!value) return null;

  const elapsedMs = Date.now() - new Date(value).getTime();

  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return null;

  const minutes = Math.floor(elapsedMs / 60000);

  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours}h`;

  return `${Math.floor(hours / 24)}d`;
};

const isStale = (value, thresholdMinutes) => {
  if (!value) return false;

  const elapsedMinutes = (Date.now() - new Date(value).getTime()) / 60000;

  return elapsedMinutes >= thresholdMinutes;
};

const Board = () => {
  const classes = useStyles();
  const history = useHistory();

  const [groupBy, setGroupBy] = useState("user");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  const fetchBoard = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestIdRef.current;

      if (!silent) setLoading(true);

      try {
        const { data } = await api.get("/tickets/board", {
          params: { groupBy }
        });

        // Resposta antiga chegando depois da nova sobrescreveria o quadro com
        // dado velho - acontece ao trocar de aba com a rede lenta.
        if (requestId !== requestIdRef.current) return;

        setBoard(data);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        toastError(error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [groupBy]
  );

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Silencioso: piscar "carregando" a cada 30 segundos faria o gerente
      // perder a linha que estava lendo.
      fetchBoard({ silent: true });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchBoard]);

  const totals = board?.totals || {
    open: 0,
    pending: 0,
    unread: 0,
    agentsOnline: 0
  };

  const updatedAtLabel = useMemo(() => {
    if (!board?.generatedAt) return "";

    return new Date(board.generatedAt).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }, [board?.generatedAt]);

  const renderMetric = (label, value, icon) => (
    <Paper variant="outlined" className={classes.metricCard}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className={classes.metricLabel}>{label}</Typography>
        <span className={classes.iconMuted}>{icon}</span>
      </Box>
      <Typography className={classes.metricValue}>{value}</Typography>
    </Paper>
  );

  const renderCard = card => {
    const lastMessageAge = formatAge(card.lastMessageAt);
    const ticketAge = formatAge(card.createdAt);
    const stale = isStale(card.lastMessageAt, 30);

    return (
      <Paper
        key={card.id}
        elevation={0}
        className={classes.card}
        onClick={() => history.push(`/tickets/${card.id}`)}
      >
        <div className={classes.cardTop}>
          <span className={classes.cardName}>{card.contactName}</span>
          {card.unreadMessages > 0 && (
            <span className={classes.unreadBadge}>{card.unreadMessages}</span>
          )}
        </div>

        <div className={classes.cardTicketId}>
          #{String(card.id).padStart(6, "0")}
          {card.queueName ? ` · ${card.queueName}` : " · sem fila"}
        </div>

        {card.lastMessage ? (
          <div className={classes.cardPreview}>{card.lastMessage}</div>
        ) : null}

        <div className={classes.cardMeta}>
          {lastMessageAge && (
            <Tooltip arrow title="Tempo desde a ultima mensagem">
              <span
                className={`${classes.metaChip} ${
                  stale ? classes.metaChipStale : ""
                }`}
              >
                {lastMessageAge}
              </span>
            </Tooltip>
          )}
          {ticketAge && (
            <Tooltip arrow title="Idade do atendimento">
              <span className={classes.metaChip}>⏱ {ticketAge}</span>
            </Tooltip>
          )}
          {card.userName && groupBy === "queue" && (
            <span className={classes.metaChip}>{card.userName}</span>
          )}
          {card.connectionName && (
            <span className={classes.metaChip}>{card.connectionName}</span>
          )}
        </div>
      </Paper>
    );
  };

  const renderColumn = group => (
    <Paper
      key={group.key}
      variant="outlined"
      className={`${classes.column} ${
        group.type === "pending" ? classes.columnPending : ""
      }`}
    >
      <div className={classes.columnHeader}>
        <div className={classes.columnTitle}>
          {group.type === "user" && (
            <span
              className={classes.presenceDot}
              style={{
                background:
                  PRESENCE_COLORS[group.presence] || PRESENCE_COLORS.offline
              }}
            />
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {group.name}
          </span>
        </div>

        <div className={classes.columnCounters}>
          <span className={classes.counterChip}>{group.openCount} atendendo</span>
          {group.pendingCount > 0 && (
            <span className={classes.counterChip}>
              {group.pendingCount} aguardando
            </span>
          )}
          {group.unreadCount > 0 && (
            <span
              className={`${classes.counterChip} ${classes.counterChipUnread}`}
            >
              {group.unreadCount} nao lidas
            </span>
          )}
        </div>
      </div>

      <div className={classes.columnBody}>
        {group.cards.length === 0 ? (
          <div className={classes.emptyColumn}>Sem atendimentos</div>
        ) : (
          group.cards.map(renderCard)
        )}
        {group.hasMore && (
          <div className={classes.emptyColumn}>
            Mostrando os {group.cards.length} mais recentes
          </div>
        )}
      </div>
    </Paper>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Monitoramento</Title>
      </MainHeader>

      <Grid container spacing={1}>
        <Grid item xs={6} md={3}>
          {renderMetric(
            "Em atendimento",
            totals.open,
            <HeadsetMicOutlinedIcon fontSize="small" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {renderMetric(
            "Aguardando",
            totals.pending,
            <ScheduleOutlinedIcon fontSize="small" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {renderMetric(
            "Com nao lidas",
            totals.unread,
            <MarkunreadOutlinedIcon fontSize="small" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {renderMetric(
            "Atendentes online",
            totals.agentsOnline,
            <PeopleAltOutlinedIcon fontSize="small" />
          )}
        </Grid>
      </Grid>

      <div className={classes.toolbar}>
        <Tabs
          value={groupBy}
          onChange={(_event, value) => setGroupBy(value)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Por atendente" value="user" />
          <Tab label="Por fila" value="queue" />
        </Tabs>

        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {updatedAtLabel && (
            <span className={classes.updatedAt}>
              Atualizado as {updatedAtLabel}
            </span>
          )}
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchBoard()}
            disabled={loading}
          >
            Atualizar
          </Button>
        </Box>
      </div>

      {board?.truncated && (
        <div className={classes.truncatedWarning}>
          <WarningIcon fontSize="small" />
          Ha mais atendimentos vivos do que o quadro carrega de uma vez. Os
          contadores do topo tambem refletem apenas o que foi carregado.
        </div>
      )}

      {loading && !board ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div className={classes.boardScroller}>
          {(board?.groups || []).map(renderColumn)}
        </div>
      )}
    </MainContainer>
  );
};

export default Board;
